"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { csx } from "../../utils";
import { useTheme } from "../ThemeProvider";
import "./Waveform.css";
import { WaveformBar } from "./WaveformBar";

export interface WaveformProps {
  /**
   * Array of normalized amplitude values (0-1) representing the waveform.
   * If not provided, uses a sample sine wave.
   */
  data?: number[];

  /**
   * Whether the entire waveform is disabled.
   */
  disabled?: boolean;

  /**
   * Reason for the waveform being disabled.
   */
  disabledMessage?: string;

  /**
   * Total duration of the audio in seconds.
   */
  totalDuration: number;

  /**
   * Current playback position in seconds.
   */
  currentTime?: number;

  /**
   * Width in pixels of each waveform bar.
   */
  resolution?: number;

  /**
   * Start time to display from in seconds.
   */
  startDuration?: number;

  /**
   * End time to display until in seconds.
   */
  endDuration?: number;

  /**
   * Whether to show timestamp markers.
   */
  showTimestamps?: boolean;

  /**
   * Click handler that receives the clicked position (0-1) and event.
   */
  onClick?: (position: number, e: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Test ID for testing purposes.
   */
  "data-testid"?: string;
}

const sampleWaveform = generateSampleWaveformData(100);

/**
 * A waveform visualization component that displays audio data with interactive features.
 *
 * @component
 * @interface WaveformProps
 *
 * @returns {JSX.Element} A waveform visualization with optional timestamps and interactive features
 */

export default function Waveform({
  data: waveformData,
  resolution = 2,
  startDuration,
  endDuration,
  currentTime = 0,
  showTimestamps = false,
  totalDuration,
  disabled,
  disabledMessage,
  onClick,
  "data-testid": testId,
}: WaveformProps) {
  const { className } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [processedWaveform, setProcessedWaveform] = useState<number[]>([]);
  const [hoverPosition, setHoverPosition] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const waveform = waveformData || sampleWaveform;

  const spacing = resolution;

  useEffect(() => {
    if (!containerRef.current) return;

    const processWaveform = (width: number) => {
      const numBars = Math.floor(width / (resolution + spacing));
      const processed = resampleData(waveform, numBars);
      setProcessedWaveform(processed);
    };

    const initialWidth = containerRef.current.clientWidth;
    processWaveform(initialWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries[0]) {
        processWaveform(entries[0].contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [waveform, resolution, spacing]);

  useEffect(() => {
    const visibilityTimer = setTimeout(() => setIsVisible(true), 50);
    const animationTimer = setTimeout(() => setHasAnimated(true), 500);

    return () => {
      clearTimeout(visibilityTimer);
      clearTimeout(animationTimer);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = (x / rect.width) * totalDuration;
    setHoverPosition(position);
  };

  const handleMouseLeave = () => {
    setHoverPosition(null);
  };

  // Calculate the width of a single bar including spacing
  const barTotalWidth = resolution + spacing;

  // Find the nearest bar boundaries for start and end durations
  const getBarPosition = (time: number, direction: "start" | "end") => {
    const position = (time / totalDuration) * 100;
    const barIndex =
      direction === "start"
        ? Math.floor((position / 100) * processedWaveform.length)
        : Math.ceil((position / 100) * processedWaveform.length);

    return (
      ((barIndex * barTotalWidth) / containerRef.current?.clientWidth!) * 100
    );
  };

  const containerWidth = containerRef.current?.clientWidth;
  const waveformMessage = useMemo(() => {
    if (disabled && disabledMessage) return disabledMessage;
    if (!waveformData) return "Waveform Unavailable";

    return null;
  }, [disabled, disabledMessage, waveformData]);

  return (
    <div className={csx("proton-Waveform", className)} data-testid={testId}>
      {showTimestamps && (
        <TimestampBlock direction="left" seconds={currentTime} />
      )}
      <div
        ref={containerRef}
        className="proton-Waveform__container"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          if (!containerRef.current || disabled) return;
          const rect = containerRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const position = (x / rect.width) * totalDuration;
          onClick?.(position, e);
        }}
        data-disabled={disabled}
        data-testid="waveform-container"
      >
        {startDuration !== undefined && endDuration !== undefined && (
          <div
            className="proton-Waveform__active-region"
            style={{
              left: containerRef.current
                ? `calc(${getBarPosition(startDuration, "start")}%`
                : "0%",
              right: containerRef.current
                ? `calc(${100 - getBarPosition(endDuration, "end")}% - 2px)`
                : "0%",
            }}
          />
        )}
        {hoverPosition !== null && (
          <div
            className="proton-Waveform__hover-line"
            style={{
              left: `${(hoverPosition / totalDuration) * 100}%`,
            }}
          />
        )}
        {processedWaveform.map((amplitude, index) => {
          const position = (index / processedWaveform.length) * totalDuration;
          const isBarOutsideActiveRegion =
            startDuration !== undefined &&
            endDuration !== undefined &&
            (position < startDuration || position > endDuration);
          const isPlayed = currentTime === 0 ? false : position <= currentTime;
          const isHovered = hoverPosition !== null && position <= hoverPosition;

          return (
            <WaveformBar
              key={index}
              amplitude={amplitude}
              isDisabled={disabled || isBarOutsideActiveRegion}
              isPlayed={isPlayed}
              isHovered={isHovered}
              isVisible={isVisible}
              hasAnimated={hasAnimated}
              resolution={resolution}
              spacing={spacing}
              index={index}
            />
          );
        })}
      </div>

      {waveformMessage && (
        <div
          className="proton-Waveform__bar-unavailable"
          style={{
            fontSize: containerWidth < 325 ? "0.85rem" : "1rem",
          }}
        >
          {waveformMessage}
        </div>
      )}

      {showTimestamps && (
        <TimestampBlock direction="right" seconds={totalDuration} />
      )}
    </div>
  );
}

function TimestampBlock({
  direction,
  seconds,
}: {
  direction: "left" | "right";
  seconds: number;
}) {
  return (
    <div
      className={csx(
        "proton-Waveform__timestamp",
        `proton-Waveform__timestamp--${direction}`
      )}
    >
      {formatTimestamp(seconds)}
    </div>
  );
}

/** Formats seconds into MM:SS timestamp string
 * @param {number} seconds - Number of seconds to format
 * @returns {string} Formatted timestamp string in MM:SS format
 */

function formatTimestamp(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${
    remainingSeconds < 10 ? "0" : ""
  }${remainingSeconds.toFixed(0)}`;
}

/** Takes an array of waveform data and rescales it to any length
 * @param {array} initialData - original waveform data
 * @param {number} finalArrayLength - How long do you want the returned array?
 * @param {number} height - Height of the waveform container
 */

function resampleData(initialData: number[], finalArrayLength: number) {
  const initialArrayLength = initialData.length;
  // How many samples from the original data do we skip per new sample?
  const step = initialArrayLength / finalArrayLength;

  // Create new array with desired length
  const resampledData = new Array(finalArrayLength)
    .fill(0)
    .map((_, newIndex) => {
      // Calculate position in original array
      const position = newIndex * step;
      const leftIndex = Math.floor(position);
      const rightIndex = Math.min(leftIndex + 1, initialArrayLength - 1);

      // Calculate weights for interpolation
      const fraction = position - leftIndex;

      // Linear interpolation between adjacent samples
      const newVal =
        initialData[leftIndex] * (1 - fraction) +
        initialData[rightIndex] * fraction;

      return newVal;
    });

  return scaleData(resampledData);
}

/** Takes an array of waveform data and scales it based on the desired pixel height
 * @param {array} data – Array of waveform data
 * @param {number} height - Height of the waveform container
 */
function scaleData(data: number[]) {
  const min = Math.min(...data);
  const max = Math.max(...data);

  return data.map((item) => {
    // Normalize to 0-1 range first
    const normalized = (item - min) / (max - min);
    // Then scale to desired height (using 0.1 as minimum to ensure visibility)
    return Math.max(normalized * 0.8 + 0.1, 0.1);
  });
}

/** Generates a sample sine wave waveform data array
 * @param {number} length - Length of the waveform data array
 * @returns {number[]} Array of normalized amplitude values (0-1) representing the waveform
 */

function generateSampleWaveformData(length: number) {
  return Array.from({ length }, (_, i) => {
    const cycles = 6;
    // Create 3 complete cycles (6π total) starting at -π/2 (trough)
    const phase = (i / (length - 1)) * cycles * 2 * Math.PI - Math.PI / 2;
    return Math.sin(phase) * 0.5 + 0.5;
  });
}
